<script setup lang="ts">
import { LogoutOutlined, MenuFoldOutlined } from "@ant-design/icons-vue";
import {
  LayoutSider,
  Menu,
  Drawer,
  Avatar,
  Button,
  Tooltip,
} from "ant-design-vue";
import { useI18n } from "vue-i18n";
import logoNoneText from "@/assets/image/Logo-nonetext.svg";
import { useDefaultLayout } from "../composables/useDefaultLayout";
import { useSidebar } from "../composables/useSidebar";

const { t } = useI18n();
const {
  closeMobileSidebar,
  isCollapsed,
  isMobile,
  isMobileSidebarOpen,
  setCollapsed,
  toggleSidebar,
} = useDefaultLayout();
const { handleMenuClick, logout, menuItems, openKeys, selectedKeys, user } =
  useSidebar();

const getInitials = (name?: string) =>
  (name || "U")
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

const expandSidebar = () => setCollapsed(false);
</script>

<template>
  <LayoutSider
    v-if="!isMobile"
    class="crm-sider"
    :collapsed="isCollapsed"
    :collapsed-width="80"
    :width="256"
  >
    <div class="crm-brand" :class="{ 'crm-brand--collapsed': isCollapsed }">
      <Tooltip v-if="isCollapsed" :title="t('menu.toggle_sidebar')">
        <button
          class="crm-brand__expand"
          type="button"
          :aria-label="t('menu.toggle_sidebar')"
          :aria-expanded="false"
          @click="expandSidebar"
        >
          <span class="crm-brand__mark"
            ><img :src="logoNoneText" alt="BestPrice CRM"
          /></span>
        </button>
      </Tooltip>
      <template v-else>
        <span class="crm-brand__mark"
          ><img :src="logoNoneText" alt="BestPrice CRM"
        /></span>
        <span class="crm-brand__name">BP-CRM</span>
        <Tooltip :title="t('menu.toggle_sidebar')">
          <Button
            type="text"
            class="crm-sidebar-toggle"
            :aria-label="t('menu.toggle_sidebar')"
            @click="toggleSidebar"
          >
            <template #icon>
              <MenuFoldOutlined />
            </template>
          </Button>
        </Tooltip>
      </template>
    </div>
    <Menu
      class="crm-menu"
      mode="inline"
      :items="menuItems"
      :open-keys="isCollapsed ? [] : openKeys"
      :selected-keys="selectedKeys"
      @click="handleMenuClick"
    />
    <div
      class="crm-sider-user"
      :class="{ 'crm-sider-user--collapsed': isCollapsed }"
    >
      <Avatar :size="34">{{ getInitials(user?.fullName) }}</Avatar>
      <div v-if="!isCollapsed" class="crm-sider-user__meta">
        <strong>{{ user?.fullName }}</strong>
        <span>{{ user?.roles?.[0]?.name || t("menu.manager_fallback") }}</span>
      </div>
      <Tooltip :title="t('auth.logout')">
        <Button
          type="text"
          danger
          shape="circle"
          :aria-label="t('auth.logout')"
          @click="logout"
        >
          <template #icon><LogoutOutlined /></template>
        </Button>
      </Tooltip>
    </div>
  </LayoutSider>

  <Drawer
    v-else
    :open="isMobileSidebarOpen"
    :closable="false"
    :width="280"
    placement="left"
    root-class-name="crm-mobile-drawer"
    @close="closeMobileSidebar"
  >
    <div class="crm-brand">
      <span class="crm-brand__mark"
        ><img :src="logoNoneText" alt="BestPrice CRM"
      /></span>
      <span class="crm-brand__name">BP-CRM</span>
    </div>
    <Menu
      class="crm-menu"
      mode="inline"
      :items="menuItems"
      :open-keys="openKeys"
      :selected-keys="selectedKeys"
      @click="handleMenuClick"
    />
    <div class="crm-sider-user crm-sider-user--mobile">
      <Avatar :size="36">{{ getInitials(user?.fullName) }}</Avatar>
      <div class="crm-sider-user__meta">
        <strong>{{ user?.fullName }}</strong>
        <span>{{ user?.roles?.[0]?.name || t("menu.manager_fallback") }}</span>
      </div>
      <Button
        type="text"
        danger
        shape="circle"
        :aria-label="t('auth.logout')"
        @click="logout"
      >
        <template #icon><LogoutOutlined /></template>
      </Button>
    </div>
  </Drawer>
</template>
